import React, { useState, useEffect } from 'react';
import { Save, Clock, Calendar } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { useOrganization } from '../../hooks/useOrganization';
import { 
  DATE_FORMAT_OPTIONS, 
  TIME_FORMAT_OPTIONS, 
  LOCALE_OPTIONS
} from '../../utils/dateFormat';

const DateTimeSettings = () => {
  const { organization, refetch } = useOrganization();
  const [settings, setSettings] = useState({
    dateFormat: 'DD-MM-YYYY',
    timeFormat: '24h',
    locale: 'en-GB'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (organization) {
      setSettings({
        dateFormat: organization.dateFormat || 'DD-MM-YYYY',
        timeFormat: organization.timeFormat || '24h',
        locale: organization.locale || 'en-GB'
      });
    }
  }, [organization]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.put('/api/organization', settings);
      
      // Refetch organization data
      refetch();
      
      // Update localStorage for immediate effect
      const orgData = JSON.parse(localStorage.getItem('organization') || '{}');
      localStorage.setItem('organization', JSON.stringify({
        ...orgData,
        ...settings
      }));
      
      toast.success('Date & time settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const previewDate = new Date();

  const getPreviewDate = (format) => {
    const d = previewDate.getDate().toString().padStart(2, '0');
    const m = (previewDate.getMonth() + 1).toString().padStart(2, '0');
    const y = previewDate.getFullYear();
    
    switch (format) {
      case 'DD-MM-YYYY': return `${d}-${m}-${y}`;
      case 'MM-DD-YYYY': return `${m}-${d}-${y}`;
      case 'YYYY-MM-DD': return `${y}-${m}-${d}`;
      case 'DD/MM/YYYY': return `${d}/${m}/${y}`;
      case 'MM/DD/YYYY': return `${m}/${d}/${y}`;
      default: return `${d}-${m}-${y}`;
    }
  };

  const getPreviewTime = (format) => {
    const h = previewDate.getHours();
    const min = previewDate.getMinutes().toString().padStart(2, '0');
    
    if (format === '12h') {
      const h12 = h % 12 || 12;
      const ampm = h >= 12 ? 'PM' : 'AM';
      return `${h12}:${min} ${ampm}`;
    }
    return `${h.toString().padStart(2, '0')}:${min}`;
  };

  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Date & Time Format</h3>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Configure how dates and times are displayed throughout the application
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Date Format */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date Format
          </label>
          <select
            value={settings.dateFormat}
            onChange={(e) => setSettings({ ...settings, dateFormat: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {DATE_FORMAT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Preview: {getPreviewDate(settings.dateFormat)}
          </p>
        </div>

        {/* Time Format */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Time Format
          </label>
          <select
            value={settings.timeFormat}
            onChange={(e) => setSettings({ ...settings, timeFormat: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {TIME_FORMAT_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Preview: {getPreviewTime(settings.timeFormat)}
          </p>
        </div>

        {/* Locale */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Locale
          </label>
          <select
            value={settings.locale}
            onChange={(e) => setSettings({ ...settings, locale: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {LOCALE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Preview Section */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <Clock className="w-4 h-4 mr-2" />
            Preview
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Date:</span>
              <span className="font-mono">{getPreviewDate(settings.dateFormat)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Time:</span>
              <span className="font-mono">{getPreviewTime(settings.timeFormat)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Date & Time:</span>
              <span className="font-mono">{getPreviewDate(settings.dateFormat)} {getPreviewTime(settings.timeFormat)}</span>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateTimeSettings;
